/**
 * A gantt: one row per line, each with a bar between its start and its end.
 *
 * The rows are the row window, the same one the grid uses. What is particular
 * to a gantt is the axis, and it is a *range the reader is looking at* rather
 * than anything derived from the rows -- see `timeline.ts` for why. Moving it is
 * a screenful at a time, at the timescale the view stores.
 *
 * Read-only for now: a bar shows a row and opens it. Dragging one to move its
 * dates is a write to two columns and arrives with the rest of Phase B.
 */
import { useEffect, useRef, useState, type UIEvent } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { cn } from '@/shared/lib/utils'
import { rowColors } from '../decorations/colors'
import { parseDateValue } from '../fields/dates'
import { cellValue } from '../fields/read'
import { useFieldContext } from '../fields/ui/context'
import { CellDisplay } from '../fields/ui/read'
import { RowModal } from '../row/row-modal'
import {
  useRegisteredRowWindow,
  useRowQuery,
  useRowWindow,
  useRowWindowStore,
} from '../grid/use-grid'
import type { RowSearch } from '../rows/rowQuery'
import { rowAt } from '../rows/rowWindow'
import { hiddenFields, visibleFields, type FieldPlacement } from '../views/field-order'
import { useRowDialog } from '../views/use-row-dialog'
import { fetchGanttRows } from './fetcher'
import {
  TIMESCALE_COLUMNS,
  addUnits,
  barFor,
  columnLabel,
  startOfUnit,
  timelineColumns,
  timelineRange,
  type Timescale,
} from './timeline'

/** One line, which is one row: fixed, so the lines can be virtualised. */
const ROW_HEIGHT = 36

export interface GanttViewProps {
  view: ViewOf<'gantt'>
  fields: readonly Field[]
  fieldOptions: Record<string, FieldPlacement>
  search: RowSearch
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
  /** Where the axis opens. A parameter so a test does not depend on today. */
  initialAnchor?: Date
}

export function GanttView({
  view,
  fields,
  fieldOptions,
  search,
  openRowId,
  onOpenRow,
  initialAnchor,
}: GanttViewProps) {
  const { t } = useTranslation()
  const store = useRowWindowStore(fetchGanttRows)
  useRowQuery(store, view, search)
  const member = useRegisteredRowWindow(store, {
    tableId: view.table_id,
    viewId: view.id,
    fields,
    view,
  })

  const context = useFieldContext(fields[0]?.workspace_id)
  const scroller = useRef<HTMLDivElement>(null)
  const timescale: Timescale = view.timescale ?? 'day'
  const [anchor, setAnchor] = useState(() =>
    startOfUnit(initialAnchor ?? new Date(), timescale),
  )

  const count = useRowWindow(store, (state) => state.window.count)
  const answered = useRowWindow(store, (state) => state.answered)
  const visible = useRowWindow(store, (state) => state.visible)
  const window = useRowWindow(store, (state) => state.window)

  useEffect(() => {
    const element = scroller.current
    if (element === null) return
    const report = () => {
      store
        .getState()
        .setViewport({ height: element.clientHeight, rowHeight: ROW_HEIGHT })
    }
    report()
    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [store])

  const openRow = useRowDialog({
    window,
    count,
    member,
    view,
    fields,
    openRowId,
    onOpenRow,
    search,
  })

  const startField = fields.find((field) => field.id === view.start_date_field) ?? null
  const endField = fields.find((field) => field.id === view.end_date_field) ?? null

  // The backend stores a gantt before either column has been chosen, and a
  // column can be deleted after it was. Both leave nothing to draw a bar from.
  if (startField === null || endField === null) {
    return (
      <Empty className="p-6">
        <EmptyHeader>
          <EmptyTitle>{t('gantt.unconfiguredTitle')}</EmptyTitle>
          <EmptyDescription>{t('gantt.unconfiguredDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  const shown = visibleFields(fields, fieldOptions)
  const title = shown[0] ?? null
  const columns = timelineColumns(anchor, timescale)
  const range = timelineRange(anchor, timescale)
  const step = TIMESCALE_COLUMNS[timescale]

  const indexes: number[] = []
  for (let index = visible.start; index < visible.end; index += 1) indexes.push(index)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b p-2">
        <Button
          variant="outline"
          size="icon"
          aria-label={t('gantt.earlier')}
          onClick={() => {
            setAnchor(addUnits(anchor, timescale, -step))
          }}
        >
          <ChevronLeft />
        </Button>
        <div className="text-sm font-medium">
          {t('gantt.spanning', {
            from: columnLabel(columns[0] as Date, timescale),
            to: columnLabel(columns[columns.length - 1] as Date, timescale),
          })}
        </div>
        <Button
          variant="outline"
          size="icon"
          aria-label={t('gantt.later')}
          onClick={() => {
            setAnchor(addUnits(anchor, timescale, step))
          }}
        >
          <ChevronRight />
        </Button>
      </div>

      <div className="grid shrink-0 grid-cols-[minmax(8rem,1fr)_3fr] border-b">
        <div className="text-muted-foreground truncate border-r p-2 text-xs font-medium">
          {title?.name ?? t('gantt.rowColumn')}
        </div>
        <div className="text-muted-foreground flex text-[11px]" aria-hidden>
          {columns.map((column) => (
            <div
              key={column.toISOString()}
              className="flex-1 truncate border-l px-1 py-2 first:border-l-0"
            >
              {columnLabel(column, timescale)}
            </div>
          ))}
        </div>
      </div>

      <div
        ref={scroller}
        onScroll={(event: UIEvent<HTMLDivElement>) => {
          store.getState().scrolled(event.currentTarget.scrollTop)
        }}
        className="min-h-0 flex-1 overflow-auto"
        role="table"
        aria-label={t('gantt.label')}
        aria-rowcount={count}
      >
        {count === 0 && answered ? (
          <Empty className="p-6">
            <EmptyHeader>
              <EmptyTitle>{t('gantt.emptyTitle')}</EmptyTitle>
              <EmptyDescription>{t('gantt.emptyDescription')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="relative" style={{ height: count * ROW_HEIGHT }}>
            {indexes.map((index) => {
              const row = rowAt(window, index)
              if (row === undefined) {
                return (
                  <div
                    key={index}
                    data-testid={`gantt-placeholder-${index}`}
                    className="absolute inset-x-0 border-b p-2"
                    style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
                  >
                    <div className="bg-muted/40 h-full w-1/4 animate-pulse rounded" />
                  </div>
                )
              }

              const bar = barFor(
                parseDateValue(cellValue(startField, row)),
                parseDateValue(cellValue(endField, row)),
                range,
              )
              const colors = rowColors(view.decorations ?? [], row, fields, context)

              return (
                <div
                  key={row.id}
                  role="row"
                  aria-rowindex={index + 1}
                  data-testid={`gantt-row-${row.id}`}
                  className="absolute inset-x-0 grid grid-cols-[minmax(8rem,1fr)_3fr] border-b"
                  style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
                >
                  <button
                    type="button"
                    role="cell"
                    onClick={() => {
                      onOpenRow(row.id)
                    }}
                    className="hover:bg-muted/30 focus-visible:ring-ring truncate border-r p-2 text-left text-sm focus-visible:ring-2 focus-visible:outline-none"
                  >
                    {title === null ? (
                      `#${row.id}`
                    ) : (
                      <CellDisplay field={title} row={row} context={context} />
                    )}
                  </button>
                  <div role="cell" className="relative flex items-center px-1">
                    {bar === null ? (
                      // Listed with no bar rather than dropped: the row is in
                      // the view, and a chart quietly showing fewer rows than
                      // the view says it holds is the harder thing to notice.
                      <span
                        data-testid={`gantt-nobar-${row.id}`}
                        className="text-muted-foreground text-xs"
                      >
                        {t('gantt.outsideRange')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`gantt-bar-${row.id}`}
                        aria-label={t('gantt.openRow')}
                        onClick={() => {
                          onOpenRow(row.id)
                        }}
                        className={cn(
                          'bg-primary absolute h-3 rounded',
                          // Square where it runs off the edge, so a clipped bar
                          // does not read as a task that ends there.
                          bar.continuesBefore && 'rounded-l-none',
                          bar.continuesAfter && 'rounded-r-none',
                        )}
                        style={{
                          left: `${bar.from * 100}%`,
                          // A bar with no width is a bar nobody can see, so a
                          // task inside one column keeps a sliver of the strip.
                          width: `max(${(bar.to - bar.from) * 100}%, 0.5rem)`,
                          ...(colors.background === null
                            ? {}
                            : { backgroundColor: colors.background }),
                        }}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {openRowId !== null && (
        <RowModal
          member={openRow.member}
          rowId={openRowId}
          row={openRow.row}
          missing={openRow.missing}
          fields={shown}
          hidden={hiddenFields(fields, fieldOptions)}
          context={context}
          steps={openRow.steps}
          onStep={openRow.onStep}
          onClose={() => {
            onOpenRow(null)
          }}
        />
      )}
    </div>
  )
}
